import React from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, RouteComponentProps, Switch } from 'react-router-dom';

import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ServerUrls } from '~commonInfra/url/ServerUrls';
import { MapStateToProps } from '~commonInfra/utils/ConnectUtils';
import { ServerAdminProfilesSpaceProvider } from '~src/adminProfiles/components/ServerAdminProfilesSpaceProvider';
import { BackgroundJobsSpaceProvider } from '~src/backgroundJob/components/BackgroundJobsSpaceProvider';
import { parseJobIdQueryParam } from '~src/backgroundJob/utils/BackgroundJobUrls';
import { siteUrlsSelector } from '~src/bootstrap/redux/selectors/SiteUrlsSelectors';
import { AllSchedulesSpaceProvider } from '~src/schedule/components/AllSchedulesSpaceProvider';
import { ResourceGovernanceSubspaceName } from '~src/schedule/components/ResourceGovernanceSubspace';
import { ScheduleSpaceRouter } from '~src/schedule/components/ScheduleRouter';
import { ServerStatusSpaceProvider } from '~src/serverStatus/components/ServerStatusSpaceProvider';
import { AuthLevel, getSessionManagementServiceInstance, SessionManagementService } from '~src/sessionManagement/SessionManagementService';
import { IntegrationsSubspaceName } from '~src/settings/IntegrationsSettings/IntegrationsSettingsSubspace';
import { MobileSettingsSubspaceName } from '~src/settings/mobile-settings/MobileSecuritySettingsShared';
import { AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater } from '~src/spacesPlatform/components/AnalysisVizSpace/AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';
import { ServerSettingsSubspaceName } from '~src/spacesPlatform/state/SpaceInfo';
import { ServerTasksSpaceRouter } from '~src/tasks/components/serverTasksSpace/ServerTasksSpaceRouter';
import { TaskDetailsSpaceProvider } from '~src/tasks/components/taskDetailsSpace/TaskDetailsSpaceProvider';

import { AllServerUsersAndIdentitiesSpaceRouter } from '../../../authentication/components/userIdentitiesSpace/ServerUsers/AllServerUsersAndIdentitiesSpaceRouter';
import { AppState } from '../../../bootstrap/redux/state/AppState';
import { ConnectedReduxRouterErrorWrapper } from '../../../bootstrap/routerHelpers/ReduxRouterErrorWrapper';
import { RouterError } from '../../../bootstrap/routerHelpers/RouterError';
import { isSingleSiteServerWithIntegrations } from '../../../settings/components/subspaces/ServerSettingsSpace/ServerSettingsSpace';
import { ConnectedServerSettingsSpaceProvider } from '../../../settings/components/subspaces/ServerSettingsSpace/ServerSettingsSpaceProvider';
import { AllSitesSpaceProvider } from '../../../siteManagement/components/allSitesSpace/AllSitesSpaceProvider';
import { AllServerUsersSpaceProvider } from '../../../userManagement/users/components/serverAllUsersSpace/AllServerUsersSpaceProvider';
import { ServerUserSpaceProvider } from '../../../userManagement/users/components/serverUserSpace/ServerUserSpaceProvider';



export interface ServerSpaceRouterOwnProps {
  readonly routeLocationPath: string;
  readonly routeLocationSearch: string;
}

export interface ServerSpaceRouterStateProps {
  readonly urls: SpacesSiteUrls;
  readonly routerReactKey: number;
}

export type ServerSpaceRouterComponentProps = ServerSpaceRouterOwnProps & ServerSpaceRouterStateProps;

export class ServerSpaceRouterComponent extends React.PureComponent<ServerSpaceRouterComponentProps> {
  private readonly server: SessionManagementService;

  public constructor(props: ServerSpaceRouterComponentProps) {
    super(props);
    this.server = getSessionManagementServiceInstance();
  }

  public render() {
    return (
      <div className="tb-fill">
        <Switch>
          { this.server.isUserAuthorized(AuthLevel.ServerAdmin) &&
            this.routes()
          }
          <Route render={this.error}/>
        </Switch>
      </div>
    );
  }

  private routes = () => {
    return (
      <Switch>
        <Route path={ServerUrls.getInstance().sitesPath()} render={this.serverSitesSpace}/>
        {
          FeatureFlags.jobManagementEnabled && FeatureFlags.backgroundJobsContentAndTaskDetailsEnabled &&
          <Route
            exact path={`${ServerUrls.getInstance().jobsPath()}/task/:taskLuid`}
            render={(routeProps) => <TaskDetailsSpaceProvider
              taskLuid={routeProps.match.params.taskLuid}
              jobsUrl={ServerUrls.getInstance().jobsUrl()}
              jobId={parseJobIdQueryParam(routeProps.location.search)}
            />}
          />
        }
        {
          FeatureFlags.jobManagementEnabled &&
          <Route
            path={ServerUrls.getInstance().jobsPath()}
            render={(routeProps) => <BackgroundJobsSpaceProvider
              siteName={undefined}
              routeLocationSearch={routeProps.location.search}
              routeLocationPath={routeProps.location.pathname}
            />}
          />
        }
        <Route
          exact path={ServerUrls.getInstance().schedulesPath()}
          render={(routeProps) => <AllSchedulesSpaceProvider
            siteName={undefined}
            routeLocationSearch={routeProps.location.search}
            routeLocationPath={routeProps.location.pathname}
            urls={ServerUrls.getInstance()}
          />}
        />
        <Route
          path={ServerUrls.getInstance().schedulePath(':id')}
          render={() => <ScheduleSpaceRouter
            siteName={undefined}
            urls={ServerUrls.getInstance()}
          />}
        />
        {
          // Since the "/" is allowed in usernames historically back to early Tableau Server versions, we want to support
          // routing in these cases. However, by default, React Router doesn't handle this character in a way we want.
          // The "+" notation is a way to make the matching greedy, whereas without it we would break at all "/" characters,
          // which is the way React Router natively interprets matches.
          // See:
          // https://github.com/ReactTraining/react-router/issues/313
          // and
          // https://stackoverflow.com/questions/56133182/allow-slashes-in-route-parameter-on-react
        }
        <Route
          exact path={decodeURIComponent(ServerUrls.getInstance().userSettingsPath(':domainName', ':username+'))}
          render={(routeProps) => <ServerUserSpaceProvider
            domainName={routeProps.match.params.domainName}
            // Need to decode username as special characters like space, #, etc are allowed for an username and are decoded in the url
            userName={decodeURIComponent(routeProps.match.params.username)}
          />}
        />
        <Route
          exact path={decodeURIComponent(ServerUrls.getInstance().userPath(':domainName', ':username+'))}
          render={({match}) => <Redirect
            to={ServerUrls.getInstance().userSettingsPath(match.params.domainName, match.params.username)}/>}
        />

        {this.getAllServerUsersSpaceRoute()}

        {this.getServerStatusVizRoute(this.server)}
        {
          FeatureFlags.extensionsSettingsTabEnabled &&
          <Route exact path={ServerUrls.getInstance().settingsExtensionsPath()}
                 render={this.settingsProvider('extensions')}/>
        }

        {
          FeatureFlags.packagesEnabled &&
          !this.server.doesTheServerHaveMoreThanOneSite() &&
          <Route exact path={ServerUrls.getInstance().settingsPackagesPath()}
                 render={this.settingsProvider('packages')}/>
        }

        {
          !this.server.doesTheServerHaveMoreThanOneSite() &&
          <Route exact path={ServerUrls.getInstance().settingsConnectedApplicationsPath()} render={this.settingsProvider('connectedApplications')} />
        }
        <Route exact path={ServerUrls.getInstance().settingsPath()} render={this.settingsProvider('settings')}/>
        <Route exact path={ServerUrls.getInstance().settingsLicensesPath()} render={this.settingsProvider('licenses')}/>
        <Route exact path={ServerUrls.getInstance().settingsCustomizationPath()} render={this.settingsProvider('customization')}/>
        {
          (isSingleSiteServerWithIntegrations() || FeatureFlags.instantAnalyticsOnPremMilestone3) &&
          <Route exact path={ServerUrls.getInstance().settingsIntegrationsPath()}
                 render={this.settingsProvider(IntegrationsSubspaceName)}/>
        }
        {
          FeatureFlags.onPremiseGovernanceEnabled &&
          <Route exact path={ServerUrls.getInstance().settingsResourceLimitsPath()}
            render={this.settingsProvider(ResourceGovernanceSubspaceName)}/>
        }
        {
          !this.server.doesTheServerHaveMoreThanOneSite() &&
          <Route exact path={ServerUrls.getInstance().addSitePath()} render={this.settingsProvider('addsite')}/>
        }
        <Route exact path={ServerUrls.getInstance().settingsMobilePath()} render={this.settingsProvider(MobileSettingsSubspaceName)}/>
        <Route
          path={ServerUrls.getInstance().statusPath()}
          render={() => <ServerStatusSpaceProvider/>}
        />
        {
          this.server.isAdminProfilesEnabled() &&
          <Route
            path={`${ServerUrls.getInstance().adminProfilesPath()}/:profileId?/:subspace?`}
            render={(routeProps) => <ServerAdminProfilesSpaceProvider
              profileId={routeProps.match.params.profileId || null}
              subspace={routeProps.match.params.subspace || 'permissions'}
            />}
          />
        }
        <Route path={ServerUrls.getInstance().tasksPath()} render={() => <ServerTasksSpaceRouter {...this.props} />}/>
        <Redirect exact from={ServerUrls.getInstance().serverPath('')} to={ServerUrls.getInstance().statusPath()}/>
        <Route render={this.error}/>
      </Switch>
    );
  };

  private error = (): JSX.Element => {
    if (this.server.isUserAuthorized(AuthLevel.ServerAdmin)) {
      return <RouterError errorType={'server-default'} urls={this.props.urls}/>;
    } else {
      return <RouterError errorType={'default'} urls={this.props.urls}/>;
    }
  };

  private settingsProvider(subspaceName: ServerSettingsSubspaceName) {
    return () =>
      <ConnectedServerSettingsSpaceProvider
        subspaceName={subspaceName}
      />;
  }

  private serverSitesSpace = (props: RouteComponentProps): JSX.Element => (
    <AllSitesSpaceProvider
      routeLocationPath={props.location.pathname}
      routeLocationSearch={props.location.search}
    />
  );

  private getServerStatusVizRoute(server: SessionManagementService) {
    return server.isUserAuthorized(AuthLevel.SiteAdmin) &&
      <Route path={ServerUrls.getInstance().serverStatusVizPath(':sheetUrlId')}
             render={(routeProps) => <AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater.ConnectedUpdater
                sheetUrlId= {routeProps.match.params.sheetUrlId}
                routeLocationSearch={routeProps.location.search}
                history={routeProps.history}
                urls={this.props.urls}
                isServerAnalysis={true}
                renderSpaceProvider={AdminAnalysisVizSpaceBreadcrumbsAndTitleUpdater.renderProvider}
             />}
      />;
  }

  private getAllServerUsersSpaceRoute() {
    if (FeatureFlags.identityMigrationEnabled) {
      // Looks for a partial URL match and delegates on the user space router.
      return <Route
        path={ServerUrls.getInstance().usersPath()}
        render={(routeProps) => <AllServerUsersAndIdentitiesSpaceRouter
          urls={this.props.urls}
          routeLocationSearch={routeProps.location.search}
          routeLocationPath={routeProps.location.pathname}
        />}
      />;
    } else {
      // Looks for the exact URL match and renders the space.
      return <Route
        exact path={ServerUrls.getInstance().usersPath()}
        render={(routeProps) => <AllServerUsersSpaceProvider
          routeLocationSearch={routeProps.location.search}
          routeLocationPath={routeProps.location.pathname}
        />}
      />;
    }
  }
}

const mapStateToProps: MapStateToProps<ServerSpaceRouterStateProps> = (state: AppState) => ({
  urls: siteUrlsSelector(state),
  routerReactKey: state.routerSpaceReactKey
});

export const ConnectedServerSpaceRouter: React.ComponentClass<ServerSpaceRouterOwnProps>
  = connect(mapStateToProps)(ServerSpaceRouterComponent);

export interface ServerSpaceRouterProps extends ServerSpaceRouterOwnProps {}

export class ServerSpaceRouter extends React.PureComponent<ServerSpaceRouterProps> {
  public render() {
    return (
      <ConnectedReduxRouterErrorWrapper>
        <ConnectedServerSpaceRouter
          routeLocationPath={this.props.routeLocationPath}
          routeLocationSearch={this.props.routeLocationSearch}
        />
      </ConnectedReduxRouterErrorWrapper>
    );
  }
}
